import type { PrV2CommentResponse } from '@/lib/pr-v2/types';

import type { PrV2VersionOption } from '../hooks/use-pr-v2-version-comparison';

import { asRecord } from '@/lib/pr-v2/utils/as-record';

export type PrV2CommentComparison = {
  baseSha: string;
  headSha: string;
};

function areShasEqual(left: string | null | undefined, right: string | null | undefined): boolean {
  return Boolean(left && right && left.toLowerCase() === right.toLowerCase());
}

function findVersionOption(
  versionOptions: PrV2VersionOption[],
  sha: string | null | undefined,
): PrV2VersionOption | null {
  if (!sha) {
    return null;
  }

  return versionOptions.find((option) => areShasEqual(option.id, sha)) ?? null;
}

export function getPrV2CommentCommitSha(
  comment: PrV2CommentResponse | null | undefined,
): string | null {
  const raw = asRecord(comment?.raw);
  const commitSha = raw?.commit_id;

  return typeof commitSha === 'string' && commitSha.trim() ? commitSha.trim() : null;
}

export function resolvePrV2CommentComparison({
  canonicalBaseSha,
  canonicalHeadSha,
  currentBaseSha,
  currentHeadSha,
  targetHeadSha,
  versionOptions,
}: {
  canonicalBaseSha: string | null;
  canonicalHeadSha: string | null;
  currentBaseSha: string | null;
  currentHeadSha: string | null;
  targetHeadSha: string | null;
  versionOptions: PrV2VersionOption[];
}): PrV2CommentComparison | null {
  if (!targetHeadSha) {
    return null;
  }

  const canonicalBaseOption = findVersionOption(versionOptions, canonicalBaseSha);
  const canonicalHeadOption = findVersionOption(versionOptions, canonicalHeadSha);
  const currentBaseOption = findVersionOption(versionOptions, currentBaseSha);
  const currentHeadOption = findVersionOption(versionOptions, currentHeadSha);
  const targetOption = findVersionOption(versionOptions, targetHeadSha);
  if (
    !canonicalBaseOption ||
    !canonicalHeadOption ||
    !currentBaseOption ||
    !currentHeadOption ||
    !targetOption
  ) {
    return null;
  }

  const isInsideCurrentSpan =
    targetOption.index > currentBaseOption.index && targetOption.index <= currentHeadOption.index;
  if (isInsideCurrentSpan) {
    return null;
  }

  return {
    baseSha: canonicalBaseOption.id,
    headSha: canonicalHeadOption.id,
  };
}
